import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { UserEntity } from './user.entity';

/**
 * PD-14960 — a phone number a rep pinned to the Favorites tab of their
 * softphone dialer, the way a phone keeps starred contacts at hand.
 *
 * Kept on the server, per tenant user, so favorites follow the rep across
 * browsers and the mobile app. The number is stored in E.164 (the server
 * normalizes it and refuses anything that will not normalize), so the same
 * number typed two ways is one favorite.
 *
 * `matchType` / `matchEntityId` remember the client or vendor the number
 * resolved to when it was pinned, and `label` the name shown then. They are a
 * hint only: the dialer resolves every number again on each read, through the
 * same caller-ID matcher (and its company restrictions) as Recents, so a
 * record the rep can no longer see is never shown from here.
 *
 * One row per (user, number). Unpinning soft-deletes the row and pinning the
 * number again recovers it, so the unique key is never violated.
 */
@Index('idx_softphone_favorite_user_phone', ['fkUserId', 'phoneE164'], {
	unique: true,
})
@Entity('softphone_favorite')
export class SoftphoneFavoriteEntity extends BaseEntity {
	/** Tenant-local `user._id`, the id the session's instance user carries. */
	@Column('int', { name: 'fk_user__id', unsigned: true })
	public fkUserId!: number;

	@Column('varchar', { name: 'phoneE164', length: 32 })
	public phoneE164!: string;

	/** 'client' | 'vendor' when the number matched a record when pinned. */
	@Column('varchar', { name: 'matchType', length: 16, nullable: true })
	public matchType!: 'client' | 'vendor' | null;

	@Column('int', { name: 'matchEntityId', unsigned: true, nullable: true })
	public matchEntityId!: number | null;

	/** The name shown for the number when it was pinned. */
	@Column('varchar', { name: 'label', length: 255, nullable: true })
	public label!: string | null;

	/** Position in the rep's list, lowest first; a new favorite goes last. */
	@Column('int', { name: 'sortOrder', default: () => "'0'" })
	public sortOrder!: number;

	@ManyToOne(() => UserEntity, {
		onDelete: 'CASCADE',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_user__id', referencedColumnName: 'id' }])
	public user!: UserEntity;
}
